import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  Activity, Loader2, AlertTriangle, TrendingUp, Sparkles, Lightbulb, CalendarDays, Heart,
  Pill, FlaskConical, Stethoscope, FileText, BookOpen, Thermometer, Wind, Scale, Ruler,
  Droplet, Gauge, ArrowUpRight, ArrowDownRight, Minus,
} from 'lucide-react';
import RightDrawer from './RightDrawer';
import PharmacyAuditTrail from './PharmacyAuditTrail';
import { Button } from './ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { useTenant } from '../contexts/TenantContext';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: API, withCredentials: true });

/**
 * VitalsHistoryDrawer — doctor's command-center view of a patient's full
 * clinical history across visits. Three tabs:
 *
 *  1. Overview — Latest snapshot + sparkline trends (vitals at a glance)
 *  2. Compare  — vitals-as-rows × visit-dates-as-columns matrix for
 *                trajectory analysis
 *  3. Timeline — chronological visit cards with chief complaint,
 *                diagnosis, prescriptions (with doses) and
 *                investigations ordered + results.
 *
 * Top-right action: AI clinical review (Claude Sonnet 4.5).
 */
export default function VitalsHistoryDrawer({ open, onClose, patient }) {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ai, setAi] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [tab, setTab] = useState('overview');
  const { tenant } = useTenant();
  const brand = tenant?.branding?.primary_color || '#0f766e';

  useEffect(() => {
    if (!open || !patient?.id) return;
    setLoading(true); setAi(null); setTab('overview');
    ax.get(`/api/clinic-ops/patients/${patient.id}/history`)
      .then(({ data }) => setVisits((data?.visits || []).slice().reverse())) // oldest → newest
      .catch(() => setVisits([]))
      .finally(() => setLoading(false));
  }, [open, patient?.id]);

  // Series for sparklines (oldest → newest).
  const series = useMemo(() => visits.map(v => ({
    consultation_id: v.consultation_id,
    date: v.date,
    doctor_name: v.doctor_name,
    chief_complaint: v.chief_complaint,
    ...v.vitals,
  })), [visits]);

  const runAi = async () => {
    if (!patient?.id) return;
    setAiLoading(true);
    try {
      const { data } = await ax.post(`/api/clinic-ops/patients/${patient.id}/vitals/ai-review`, {});
      setAi(data);
      setTab('overview'); // surface the AI panel on the overview tab
    } catch (_) {
      setAi({ summary: 'AI review failed. Please try again.', red_flags: [], trends: [], recommendations: [] });
    }
    setAiLoading(false);
  };

  return (
    <RightDrawer
      open={open}
      onClose={onClose}
      title={patient?.name ? `${patient.name} · Visit history` : 'Visit history'}
      subtitle={`${visits.length} visit${visits.length === 1 ? '' : 's'} on record${patient?.age ? ` · ${patient.age}y` : ''}`}
      icon={<Activity className="h-5 w-5 text-white" />}
      accent={brand}
      data-testid="vitals-drawer"
      headerExtras={
        <Button
          size="sm"
          onClick={runAi}
          disabled={aiLoading || series.length === 0}
          className="bg-white/15 hover:bg-white/30 text-white border-0 backdrop-blur"
          data-testid="vitals-ai-btn"
        >
          {aiLoading
            ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Reviewing…</>
            : <><Sparkles className="h-3.5 w-3.5 mr-1" /> AI clinical review</>}
        </Button>
      }
    >
      <div className="p-6" data-testid="vitals-drawer-body">
        {loading ? (
          <Loading text="Loading visit history…" />
        ) : visits.length === 0 ? (
          <Empty />
        ) : (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid w-full grid-cols-3 mb-5">
              <TabsTrigger value="overview" data-testid="vh-tab-overview">
                <Heart className="h-3.5 w-3.5 mr-1.5" /> Overview
              </TabsTrigger>
              <TabsTrigger value="compare" data-testid="vh-tab-compare">
                <TrendingUp className="h-3.5 w-3.5 mr-1.5" /> Compare
              </TabsTrigger>
              <TabsTrigger value="timeline" data-testid="vh-tab-timeline">
                <CalendarDays className="h-3.5 w-3.5 mr-1.5" /> Timeline
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-5">
              {ai && <AiPanel ai={ai} />}
              <HistorySummary visits={visits} brand={brand} />
              <VitalCardGrid series={series} brand={brand} />
            </TabsContent>

            <TabsContent value="compare">
              <CompareTable visits={visits} brand={brand} />
            </TabsContent>

            <TabsContent value="timeline" className="space-y-3">
              {[...visits].reverse().map((v, i) => (
                <VisitCard key={v.consultation_id || i} visit={v} brand={brand} />
              ))}
            </TabsContent>
          </Tabs>
        )}
      </div>
    </RightDrawer>
  );
}

// ============================================================================
// Reusable bits
// ============================================================================

function Loading({ text }) {
  return (
    <div className="py-16 text-center text-zinc-400">
      <Loader2 className="h-6 w-6 mx-auto animate-spin" />
      <p className="text-sm mt-2">{text}</p>
    </div>
  );
}

function Empty() {
  return (
    <div className="py-16 text-center text-zinc-400">
      <Activity className="h-8 w-8 mx-auto opacity-40" />
      <p className="text-sm mt-2">No visits recorded for this patient yet.</p>
    </div>
  );
}

function AiPanel({ ai }) {
  return (
    <section className="rounded-2xl bg-gradient-to-br from-violet-50 via-white to-blue-50 border border-violet-200 p-5"
             data-testid="vitals-ai-panel">
      <div className="flex items-center gap-2 mb-3">
        <div className="h-8 w-8 rounded-lg flex items-center justify-center text-white shadow-sm"
             style={{ background: 'linear-gradient(135deg, #7c3aed, #2563eb)' }}>
          <Sparkles className="h-4 w-4" />
        </div>
        <div>
          <h3 className="font-bold text-zinc-900 text-sm leading-tight" style={{ fontFamily: 'Manrope' }}>AI Clinical Review</h3>
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Claude Sonnet 4.5 · {ai?.vitals_used || 0} visits analysed</p>
        </div>
      </div>
      {ai?.summary && <p className="text-sm text-zinc-700 leading-relaxed mb-3 italic">{ai.summary}</p>}
      {(ai?.red_flags || []).length > 0 && (
        <BulletGroup title="Red flags" icon={<AlertTriangle className="h-3.5 w-3.5" />} tone="red" items={ai.red_flags} />
      )}
      {(ai?.trends || []).length > 0 && (
        <BulletGroup title="Trends" icon={<TrendingUp className="h-3.5 w-3.5" />} tone="blue" items={ai.trends} />
      )}
      {(ai?.recommendations || []).length > 0 && (
        <BulletGroup title="Recommendations" icon={<Lightbulb className="h-3.5 w-3.5" />} tone="emerald" items={ai.recommendations} />
      )}
      {(!ai?.red_flags?.length && !ai?.trends?.length && !ai?.recommendations?.length) && (
        <p className="text-xs text-zinc-500 italic">No actionable signals from the AI review.</p>
      )}
    </section>
  );
}

function BulletGroup({ title, icon, tone, items }) {
  const tones = {
    red: 'bg-red-50 border-red-200 text-red-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  };
  return (
    <div className={`rounded-lg border ${tones[tone]} p-3 mb-2`} data-testid={`ai-${tone}`}>
      <div className="text-[10px] uppercase tracking-wider font-bold flex items-center gap-1.5 mb-1.5 opacity-80">
        {icon} {title}
      </div>
      <ul className="text-sm space-y-1 list-disc pl-4">
        {items.map((s, i) => <li key={i}>{s}</li>)}
      </ul>
    </div>
  );
}

// ============================================================================
// Overview: history summary stat strip
// ============================================================================

function HistorySummary({ visits, brand }) {
  if (!visits || visits.length === 0) return null;
  const fmt = (d) => d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', yy: '2-digit', timeZone: 'Asia/Kolkata' });
  const fmtShort = (d) => d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', timeZone: 'Asia/Kolkata' });
  const oldest = visits[0]?.date ? new Date(visits[0].date) : null;
  const newest = visits[visits.length - 1]?.date ? new Date(visits[visits.length - 1].date) : null;
  const daysSpan = (oldest && newest) ? Math.max(0, Math.round((newest - oldest) / 86400000)) : 0;
  const daysSinceLast = newest ? Math.max(0, Math.round((Date.now() - newest.getTime()) / 86400000)) : null;

  // Unique diagnoses across visits
  const dxSet = new Set();
  visits.forEach(v => {
    const d = (v.final_diagnosis || v.provisional_diagnosis || '').trim();
    if (d) dxSet.add(d.toLowerCase());
  });
  const rxTotal = visits.reduce((acc, v) => acc + ((v.prescription || []).length), 0);
  const invTotal = visits.reduce((acc, v) => acc + ((v.investigations || []).length), 0);

  const stats = [
    { label: 'Visits', value: visits.length, sub: daysSpan > 0 ? `over ${daysSpan}d` : 'first visit', color: brand },
    { label: 'Last seen', value: daysSinceLast === 0 ? 'Today' : daysSinceLast === 1 ? 'Yesterday' : `${daysSinceLast}d ago`, sub: newest ? fmtShort(newest) : '—', color: '#0891b2' },
    { label: 'Diagnoses', value: dxSet.size, sub: dxSet.size === 1 ? 'condition tracked' : 'distinct conditions', color: '#7c3aed' },
    { label: 'Medications', value: rxTotal, sub: `${invTotal} investigation${invTotal === 1 ? '' : 's'}`, color: '#dc2626' },
  ];

  return (
    <section data-testid="vh-summary">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {stats.map(s => (
          <div key={s.label} className="rounded-xl border bg-white p-3 relative overflow-hidden"
               style={{ borderColor: 'rgb(228, 228, 231)' }}>
            <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: s.color }} />
            <div className="ml-1.5">
              <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">{s.label}</div>
              <div className="text-xl font-extrabold text-zinc-900 mt-0.5 leading-none" style={{ fontFamily: 'Manrope' }}>{s.value}</div>
              <div className="text-[10px] text-zinc-400 mt-1">{s.sub}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ============================================================================
// Overview: vital cards with embedded sparkline per metric
// ============================================================================

const VITAL_DEFS = [
  { mkey: 'bp_systolic',  combo: ['bp_systolic','bp_diastolic'], label: 'Blood pressure', short: 'BP',    unit: 'mmHg',   icon: Heart,       color: '#e11d48',
    alarm: v => (v.bp_systolic >= 140) || (v.bp_diastolic >= 90),
    format: v => (v.bp_systolic && v.bp_diastolic) ? `${v.bp_systolic}/${v.bp_diastolic}` : null,
    normal: '<140/90' },
  { mkey: 'pulse',        label: 'Pulse',          short: 'HR',    unit: 'bpm',    icon: Activity,    color: '#dc2626',
    alarm: v => v.pulse && (v.pulse > 100 || v.pulse < 50),
    format: v => v.pulse ?? null,
    normal: '60–100' },
  { mkey: 'spo2',         label: 'SpO₂',           short: 'SpO₂',  unit: '%',      icon: Wind,        color: '#0891b2',
    alarm: v => v.spo2 && v.spo2 < 95,
    format: v => v.spo2 ?? null,
    normal: '≥ 95' },
  { mkey: 'temperature',  label: 'Temperature',    short: 'Temp',  unit: '°C',     icon: Thermometer, color: '#f59e0b',
    alarm: v => v.temperature && v.temperature > 38,
    format: v => v.temperature ?? null,
    normal: '36–37.5' },
  { mkey: 'weight',       label: 'Weight',         short: 'Wt',    unit: 'kg',     icon: Scale,       color: '#7c3aed',
    format: v => v.weight ?? null,
    normal: null },
  { mkey: 'bmi',          label: 'BMI',            short: 'BMI',   unit: 'kg/m²',  icon: Gauge,       color: '#0d9488',
    alarm: v => v.bmi && (v.bmi > 30 || v.bmi < 17),
    format: v => v.bmi ?? null,
    normal: '18.5–24.9' },
  { mkey: 'rbs',          label: 'Random sugar',   short: 'RBS',   unit: 'mg/dL',  icon: Droplet,     color: '#db2777',
    alarm: v => v.rbs && v.rbs > 200,
    format: v => v.rbs ?? null,
    normal: '< 200' },
  { mkey: 'height',       label: 'Height',         short: 'Ht',    unit: 'cm',     icon: Ruler,       color: '#475569',
    format: v => v.height ?? null,
    normal: null },
];

function VitalCardGrid({ series, brand }) {
  return (
    <section data-testid="vitals-latest">
      <h3 className="text-xs uppercase tracking-wider font-bold text-zinc-500 mb-3 flex items-center gap-1.5">
        <Heart className="h-3.5 w-3.5 text-rose-500" /> Latest snapshot & trend
        <span className="ml-auto normal-case tracking-normal text-[10px] font-medium text-zinc-400">
          {series.length} reading{series.length === 1 ? '' : 's'}
        </span>
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {VITAL_DEFS.map(d => (
          <VitalTrendCard key={d.mkey} def={d} series={series} brand={brand} />
        ))}
      </div>
    </section>
  );
}

function VitalTrendCard({ def, series, brand }) {
  const Icon = def.icon;
  // Find latest visit with a non-null value for this metric.
  let latestRow = null;
  for (let i = series.length - 1; i >= 0; i--) {
    const v = series[i];
    const val = def.format ? def.format(v) : v[def.mkey];
    if (val !== null && val !== undefined && val !== '') { latestRow = v; break; }
  }
  const valueDisplay = latestRow ? (def.format ? def.format(latestRow) : latestRow[def.mkey]) : null;
  const hasValue = valueDisplay !== null && valueDisplay !== undefined && valueDisplay !== '';
  const isAlarm = (latestRow && def.alarm) ? def.alarm(latestRow) : false;
  // Sparkline series (with date) for plotting + delta
  const pts = series
    .map(r => ({ v: Number(r[def.mkey]), date: r.date }))
    .filter(p => Number.isFinite(p.v) && p.v > 0);
  // Recency stamp for latest non-null value
  const stamp = latestRow?.date ? new Date(latestRow.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' }) : null;
  const trendColor = def.color || brand;

  return (
    <div
      className={`relative rounded-xl border bg-white overflow-hidden transition hover:shadow-md ${isAlarm ? 'ring-1 ring-red-300 border-red-200' : 'border-zinc-200'}`}
      data-testid={`sparkline-${def.mkey}`}
    >
      <div className="absolute left-0 top-0 bottom-0 w-1" style={{ background: isAlarm ? '#dc2626' : trendColor }} />
      <div className="pl-4 pr-3 py-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-zinc-500">
            <Icon className="h-3 w-3" style={{ color: trendColor }} />
            {def.label}
          </div>
          {def.normal && (
            <span className="text-[9px] text-zinc-400 tracking-wide" title="Normal range">
              {def.normal} {def.unit}
            </span>
          )}
        </div>
        <div className="flex items-end justify-between gap-3 mt-1.5">
          <div className="min-w-0">
            <div className={`text-2xl font-extrabold leading-none ${isAlarm ? 'text-red-700' : 'text-zinc-900'}`} style={{ fontFamily: 'Manrope' }}>
              {hasValue ? valueDisplay : <span className="text-zinc-300">—</span>}
            </div>
            <div className="text-[10px] text-zinc-400 mt-1 flex items-center gap-1 flex-wrap">
              <span>{def.unit}</span>
              {stamp && <span className="text-zinc-400">· {stamp}</span>}
              <DeltaPill pts={pts} />
            </div>
          </div>
          <MiniSparkline pts={pts} color={trendColor} alarm={isAlarm} />
        </div>
      </div>
    </div>
  );
}

function DeltaPill({ pts }) {
  if (!pts || pts.length < 2) return null;
  const latest = pts[pts.length - 1].v;
  const prev = pts[pts.length - 2].v;
  const delta = latest - prev;
  if (Math.abs(delta) < 0.05) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[9px] font-semibold text-zinc-500 bg-zinc-100 px-1.5 py-0.5 rounded-full">
        <Minus className="h-2.5 w-2.5" /> stable
      </span>
    );
  }
  const up = delta > 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${
        up ? 'text-rose-700 bg-rose-50' : 'text-emerald-700 bg-emerald-50'
      }`}
      title={`${up ? '+' : ''}${delta.toFixed(1)} vs. previous`}
    >
      {up ? <ArrowUpRight className="h-2.5 w-2.5" /> : <ArrowDownRight className="h-2.5 w-2.5" />}
      {Math.abs(delta).toFixed(Math.abs(delta) < 10 ? 1 : 0)}
    </span>
  );
}

function MiniSparkline({ pts, color, alarm }) {
  const W = 110, H = 38;
  if (!pts || pts.length === 0) {
    return (
      <svg width={W} height={H} className="opacity-40">
        <line x1={0} y1={H / 2} x2={W} y2={H / 2} stroke="#d4d4d8" strokeWidth="1" strokeDasharray="3 3" />
      </svg>
    );
  }
  if (pts.length === 1) {
    // single dot
    return (
      <svg width={W} height={H}>
        <circle cx={W / 2} cy={H / 2} r="3" fill={color} />
      </svg>
    );
  }
  const vals = pts.map(p => p.v);
  const max = Math.max(...vals), min = Math.min(...vals);
  const range = Math.max(1, max - min);
  const stepX = W / (pts.length - 1);
  const coords = pts.map((p, i) => ({
    x: i * stepX,
    y: H - ((p.v - min) / range) * (H - 6) - 3,
  }));
  const path = coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(' ');
  const area = `${path} L${W},${H} L0,${H} Z`;
  const lastIdx = coords.length - 1;
  const gradId = `g-${color.replace('#', '')}-${Math.round(Math.random() * 9999)}`;
  return (
    <svg width={W} height={H} className="flex-shrink-0">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradId})`} />
      <path d={path} fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
      {coords.map((c, i) => (
        <circle
          key={i}
          cx={c.x}
          cy={c.y}
          r={i === lastIdx ? 2.8 : 1.4}
          fill={i === lastIdx ? (alarm ? '#dc2626' : color) : color}
          stroke={i === lastIdx ? '#ffffff' : 'none'}
          strokeWidth={i === lastIdx ? 1.2 : 0}
        />
      ))}
    </svg>
  );
}

// ---- Compare table -------------------------------------------------------

const COMPARE_ROWS = [
  { key: 'bp',           label: 'BP (mmHg)',        get: v => (v.vitals?.bp_systolic && v.vitals?.bp_diastolic) ? `${v.vitals.bp_systolic}/${v.vitals.bp_diastolic}` : '—',
    alarm: v => (v.vitals?.bp_systolic >= 140) || (v.vitals?.bp_diastolic >= 90) },
  { key: 'pulse',        label: 'Pulse (bpm)',      get: v => v.vitals?.pulse || '—', alarm: v => v.vitals?.pulse && (v.vitals.pulse > 100 || v.vitals.pulse < 50) },
  { key: 'spo2',         label: 'SpO₂ (%)',         get: v => v.vitals?.spo2 || '—', alarm: v => v.vitals?.spo2 && v.vitals.spo2 < 95 },
  { key: 'temp',         label: 'Temp (°C)',        get: v => v.vitals?.temperature || '—', alarm: v => v.vitals?.temperature && v.vitals.temperature > 38 },
  { key: 'weight',       label: 'Weight (kg)',      get: v => v.vitals?.weight || '—' },
  { key: 'bmi',          label: 'BMI (kg/m²)',      get: v => v.vitals?.bmi || '—', alarm: v => v.vitals?.bmi && (v.vitals.bmi > 30 || v.vitals.bmi < 17) },
  { key: 'rbs',          label: 'RBS (mg/dL)',      get: v => v.vitals?.rbs || '—', alarm: v => v.vitals?.rbs && v.vitals.rbs > 200 },
  { key: 'cc',           label: 'Chief complaint',  get: v => v.chief_complaint || '—', narrow: false },
  { key: 'dx',           label: 'Diagnosis',        get: v => v.final_diagnosis || v.provisional_diagnosis || '—', narrow: false },
  { key: 'rx_count',     label: 'Rx (count)',       get: v => (v.prescription || []).length },
  { key: 'inv_count',    label: 'Investigations',   get: v => (v.investigations || []).length },
];

function CompareTable({ visits, brand }) {
  // Newest first column for at-a-glance trajectory
  const cols = [...visits].reverse();
  const fmt = (iso) => { try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', timeZone: 'Asia/Kolkata' }); } catch { return iso; } };
  return (
    <div className="rounded-xl bg-white border overflow-hidden" data-testid="vh-compare-table">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-zinc-50 border-b">
              <th className="text-left text-[10px] uppercase tracking-wider font-bold text-zinc-500 px-3 py-2.5 sticky left-0 bg-zinc-50">Metric</th>
              {cols.map((v, i) => (
                <th key={v.consultation_id || i} className="px-3 py-2.5 text-center min-w-[110px]">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">{fmt(v.date)}</div>
                  {i === 0 && (
                    <div className="text-[9px] text-white inline-block px-1.5 py-0.5 rounded mt-0.5"
                         style={{ background: brand }}>Latest</div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {COMPARE_ROWS.map(r => (
              <tr key={r.key} className="border-b last:border-0 hover:bg-zinc-50/60">
                <td className="text-[11px] font-bold text-zinc-700 px-3 py-2 sticky left-0 bg-white">{r.label}</td>
                {cols.map((v, i) => {
                  const val = r.get(v);
                  const isAlarm = r.alarm && r.alarm(v);
                  return (
                    <td key={i}
                        className={`px-3 py-2 text-center text-[12px] font-semibold ${isAlarm ? 'text-red-700 bg-red-50/50' : 'text-zinc-800'}`}
                        title={typeof val === 'string' ? val : ''}>
                      <span className={r.narrow === false ? 'line-clamp-2 block text-left text-[11px] font-medium' : ''}>
                        {val == null || val === '' ? '—' : val}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---- Timeline visit card -----------------------------------------------

function VisitCard({ visit, brand }) {
  const d = visit.date ? new Date(visit.date) : null;
  const stamp = d ? d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }) : '—';
  const time = d ? d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) : '';
  const dx = visit.final_diagnosis || visit.provisional_diagnosis;
  const tp = visit.treatment_plan;
  const rx = visit.prescription || [];
  const inv = visit.investigations || [];

  // Lazy-fetch dispensings (with audit trail) for this visit. Only ever
  // fires once per visit row when the user expands it.
  const [dispensings, setDispensings] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    if (visit.consultation_id) {
      ax.get(`/api/clinic-ops/dispense/by-consultation/${visit.consultation_id}`)
        .then(({ data }) => { if (!cancelled) setDispensings(data || []); })
        .catch(() => { if (!cancelled) setDispensings([]); });
    }
    return () => { cancelled = true; };
  }, [visit.consultation_id]);

  const allAudit = (dispensings || []).flatMap(d => d.audit_log || []);

  return (
    <div className="border rounded-xl bg-white overflow-hidden" data-testid={`vh-visit-${visit.consultation_id}`}>
      {/* Header strip */}
      <div className="border-l-4 px-4 py-2.5 flex items-center justify-between bg-zinc-50/60" style={{ borderColor: brand }}>
        <div>
          <div className="text-sm font-extrabold text-zinc-900" style={{ fontFamily: 'Manrope' }}>{stamp}</div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">{time}{visit.doctor_name ? ` · ${visit.doctor_name}` : ''}</div>
        </div>
        <div className="flex gap-1.5 flex-wrap justify-end">
          {visit.chief_complaint && (
            <span className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-semibold max-w-[260px] truncate" title={visit.chief_complaint}>
              <Stethoscope className="h-2.5 w-2.5 inline mr-1 -mt-0.5" />{visit.chief_complaint}
            </span>
          )}
          {visit.status && (
            <span className="text-[10px] bg-zinc-100 text-zinc-700 border border-zinc-200 px-2 py-0.5 rounded-full font-semibold capitalize">
              {visit.status}
            </span>
          )}
          {allAudit.length > 0 && (
            <span className="text-[10px] bg-amber-100 text-amber-800 border border-amber-300 px-2 py-0.5 rounded-full font-bold uppercase tracking-wider"
                  title="Pharmacy adjusted this prescription">
              Rx edited · {allAudit.length}
            </span>
          )}
        </div>
      </div>

      <div className="p-4 space-y-3">
        <VitalsRow vitals={visit.vitals} />
        {dx && (
          <Block icon={<BookOpen className="h-3 w-3" />} label={visit.final_diagnosis ? 'Final diagnosis' : 'Provisional diagnosis'}>
            <p className="text-sm text-zinc-800 leading-relaxed">{dx}</p>
          </Block>
        )}
        {rx.length > 0 && (
          <Block icon={<Pill className="h-3 w-3" />} label={`Prescription (${rx.length})`}>
            <div className="space-y-1.5">
              {rx.map((m, i) => (
                <div key={i} className="flex items-baseline gap-2 text-sm">
                  <span className="font-semibold text-zinc-900">{m.name}</span>
                  {m.dose && <span className="text-zinc-500">· {m.dose}</span>}
                  {m.timing && <span className="text-zinc-700 font-mono text-xs bg-zinc-100 px-1.5 py-0.5 rounded">{m.timing}</span>}
                  {m.intake && <span className="text-zinc-500 text-xs">{m.intake}</span>}
                  {m.duration && <span className="text-zinc-700 text-xs">× {m.duration}</span>}
                  {m.notes && <span className="text-zinc-500 text-xs italic">— {m.notes}</span>}
                </div>
              ))}
            </div>
          </Block>
        )}
        {inv.length > 0 && (
          <Block icon={<FlaskConical className="h-3 w-3" />} label={`Investigations (${inv.length})`}>
            <div className="flex flex-wrap gap-1.5">
              {inv.map((t, i) => (
                <span key={i}
                  className={`inline-flex items-center text-[11px] px-2 py-1 rounded-md font-semibold border ${
                    t.priority === 'urgent' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-blue-50 border-blue-200 text-blue-700'
                  }`} title={t.reason || ''}>
                  {t.name}{t.result ? ` · ${t.result}` : ''}
                </span>
              ))}
            </div>
          </Block>
        )}
        {(tp || visit.visit_notes) && (
          <Block icon={<FileText className="h-3 w-3" />} label="Plan & notes">
            {tp && <p className="text-sm text-zinc-800 leading-relaxed">{tp}</p>}
            {visit.visit_notes && <p className="text-xs text-zinc-500 italic leading-relaxed mt-1">{visit.visit_notes}</p>}
            {visit.review_date && (
              <p className="text-xs text-emerald-700 font-semibold mt-1">Review: {visit.review_date}</p>
            )}
          </Block>
        )}
        {/* Pharmacy audit trail — what the pharmacy adjusted on this visit's Rx */}
        {allAudit.length > 0 && (
          <Block icon={<Pill className="h-3 w-3 text-amber-600" />} label="Pharmacy adjustments">
            <PharmacyAuditTrail entries={allAudit} compact />
          </Block>
        )}
      </div>
    </div>
  );
}

function Block({ icon, label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-1.5 flex items-center gap-1.5">
        {icon}{label}
      </div>
      {children}
    </div>
  );
}

function VitalsRow({ vitals }) {
  if (!vitals) return null;
  const chips = [
    { l: 'BP',   v: (vitals.bp_systolic && vitals.bp_diastolic) ? `${vitals.bp_systolic}/${vitals.bp_diastolic}` : null, alarm: (vitals.bp_systolic >= 140 || vitals.bp_diastolic >= 90) },
    { l: 'HR',   v: vitals.pulse, alarm: vitals.pulse && (vitals.pulse > 100 || vitals.pulse < 50) },
    { l: 'SpO₂', v: vitals.spo2 ? `${vitals.spo2}%` : null, alarm: vitals.spo2 && vitals.spo2 < 95 },
    { l: 'T',    v: vitals.temperature ? `${vitals.temperature}°` : null, alarm: vitals.temperature > 38 },
    { l: 'Wt',   v: vitals.weight ? `${vitals.weight}kg` : null },
    { l: 'BMI',  v: vitals.bmi, alarm: vitals.bmi && (vitals.bmi > 30 || vitals.bmi < 17) },
    { l: 'RBS',  v: vitals.rbs, alarm: vitals.rbs && vitals.rbs > 200 },
  ].filter(c => c.v != null && c.v !== '');
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {chips.map((c, i) => (
        <span key={i} className={`text-[11px] px-2 py-1 rounded font-mono ${c.alarm ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-zinc-100 text-zinc-700'}`}>
          <span className="text-zinc-500 font-sans font-bold mr-1">{c.l}</span>{c.v}
        </span>
      ))}
    </div>
  );
}
